import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { type IUpdateAccountEmailRequest } from "@apis/accounts/types"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import {
  useAccount,
  useUpdateAccountEmailMutation,
} from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { Loader2 } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"

type TAccountEmailFormProps = {
  account_id: string
}

export const AccountEmailForm = ({ account_id }: TAccountEmailFormProps) => {
  const updateAccountEmailMutation = useUpdateAccountEmailMutation()
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()

  const getAccountQuery = useAccount(account_id)

  const form = useForm<IUpdateAccountEmailRequest>({
    mode: "onTouched",
    defaultValues: {
      membership_profile_email: {
        email: getAccountQuery.data?.email || "",
      },
    },
  })

  const handleSubmit = (data: IUpdateAccountEmailRequest) => {
    updateAccountEmailMutation.mutate(
      {
        account_id: account_id,
        request: data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully updated account email!")
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
          closeDrawer()
        },
        onError: (error) =>
          toast.error(`Error updating account email! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
      >
        <div className="tw:col-span-12">
          <FormField
            control={form.control}
            name="membership_profile_email.email"
            rules={{ required: "Email is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Enter email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
          <Button
            type="submit"
            disabled={
              updateAccountEmailMutation.isPending ||
              !form.formState.isDirty ||
              !form.formState.isValid
            }
          >
            {updateAccountEmailMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Email</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
